import { useState, useEffect, useCallback } from 'react';
import apiClient from '../../../tools/axiosConfig';
import { useTranslation } from 'react-i18next';
const useLanguagePreferences = (showError) => {
    const [language, setLanguage] = useState('English');
    const [isLoading, setIsLoading] = useState(false);
    const { t } = useTranslation();
    const handleLanguageChange = (newLanguage) => {
        setLanguage(newLanguage);
    };

    /**
     * Persist the user's language preference.
     * Optionally accepts a language override so callers can
     * save immediately after a local change without waiting
     * for React state to update.
     */
    const saveLanguagePreference = async (languageOverride) => {
        const languageToSave = languageOverride || language || 'English';
        setIsLoading(true);
        try {
            await apiClient.post(`/save-language-preference/`, {
                language: languageToSave.toLowerCase(),
            });
            setLanguage(languageToSave);
            return true;
        } catch (err) {
            console.error(err);
            
            // Extract error message from server response
            let errorMessage = t('language.languageError');
            if (err.response && err.response.data && err.response.data.error) {
                errorMessage += `: ${err.response.data.error}`;
            }
            
            showError(errorMessage);
            return false;
        } finally {
            setIsLoading(false);
        }
    };

    const getLanguagePreference = useCallback(async () => {
        try {
            const isLoggedIn = localStorage.getItem('access_token');
            if (!isLoggedIn) {
                return;
            }
            const res = await apiClient.get(`/get-language-preference/`);
            const loadedLanguage = res.data.language || 'english';
            // Capitalize first letter for display
            setLanguage(loadedLanguage.charAt(0).toUpperCase() + loadedLanguage.slice(1));
        } catch (err) {
            console.error(err);
            // Don't show error if endpoint doesn't exist yet
            if (err.response && err.response.status !== 404) {
                showError(t('language.languageError'));
            }
        }
    }, [showError, t]);

    // Load the saved preference once on mount. getLanguagePreference is
    // intentionally left out of the deps: it changes identity whenever `t`
    // changes (i.e. on every language switch), and re-running the GET mid-switch
    // races with the optimistic update + save, making the UI flip old→new→old.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        getLanguagePreference();
    }, []);

    return {
        language,
        isLoading,
        handleLanguageChange,
        saveLanguagePreference,
        getLanguagePreference
    };
};

export default useLanguagePreferences;

